import React, { createContext, useContext, useState, useRef, useCallback, useEffect, useMemo } from "react";
import api from "@/lib/api";

const ProjectContext = createContext(null);
export const useProject = () => useContext(ProjectContext);

const AUTOSAVE_DELAY = 1000;

export function ProjectProvider({ projectId, children }) {
  const [project, setProject] = useState(null);
  const [phaseData, setPhaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const [error, setError] = useState(null);

  const timer = useRef(null);
  const pending = useRef({}); // accumulated unsaved fields
  const dirty = useRef(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    api.get(`/projects/${projectId}`)
      .then((r) => {
        if (!active) return;
        setProject(r.data);
        setPhaseData(r.data.phase_data || {});
      })
      .catch(() => active && setError("notfound"))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
      if (timer.current) clearTimeout(timer.current);
      // Flush any pending edits on unmount (fire-and-forget background sync).
      if (dirty.current) {
        api.put(`/projects/${projectId}`, pending.current).catch(() => {});
      }
    };
  }, [projectId]);

  const flush = useCallback(async () => {
    if (!dirty.current) return true;
    const payload = pending.current;
    setSaving(true);
    try {
      const res = await api.put(`/projects/${projectId}`, payload);
      setProject(res.data);
      pending.current = {};
      dirty.current = false;
      setLastSavedAt(new Date());
      return true;
    } catch (e) {
      return false;
    } finally {
      setSaving(false);
    }
  }, [projectId]);

  const scheduleSave = useCallback(() => {
    dirty.current = true;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => { flush(); }, AUTOSAVE_DELAY);
  }, [flush]);

  // Update a single phase's data — synced to the active project state + autosaved.
  const setPhase = useCallback((key, value) => {
    setPhaseData((prev) => {
      const next = { ...prev, [key]: value };
      pending.current = { ...pending.current, phase_data: next };
      return next;
    });
    scheduleSave();
  }, [scheduleSave]);

  // Update project meta (name, status, current_phase).
  const updateMeta = useCallback((partial) => {
    setProject((prev) => ({ ...prev, ...partial }));
    pending.current = { ...pending.current, ...partial };
    scheduleSave();
  }, [scheduleSave]);

  // Force an immediate save (cancels debounce).
  const saveNow = useCallback(async () => {
    if (timer.current) clearTimeout(timer.current);
    return flush();
  }, [flush]);

  const value = useMemo(
    () => ({ project, phaseData, loading, saving, lastSavedAt, error, setPhase, updateMeta, saveNow }),
    [project, phaseData, loading, saving, lastSavedAt, error, setPhase, updateMeta, saveNow]
  );

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
}
